import type { ProfitabilityRowDto } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { ReportFormatters } from '../useReportFormatters.js';

/**
 * Profit & margin leaderboards (legacy ReportsPage top/worst lists — previously
 * deferred, now rendered). Four small ranked lists: top/worst by profit and
 * top/worst by margin, each row showing the job name + the ranked metric. Profit
 * rows color by sign (green `--chart-3` / red `--destructive`); the value's own
 * sign carries the meaning too (A11Y-01). Token-styled only (BRAND-06).
 *
 * Each job link carries `pointer-coarse:py-1` because the kit's tap-target floor
 * only reaches links inside a `<td>` and these sit in an `<ol>`: 20px tall is
 * under the 24px WCAG 2.5.8 asks for. Coarse-pointer only, so desktop is dense.
 */

type Metric = 'profit' | 'margin';

function LeaderList({
  heading,
  rows,
  metric,
  fmt,
}: {
  heading: string;
  rows: ProfitabilityRowDto[];
  metric: Metric;
  fmt: ReportFormatters;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h3 className="mb-3 font-semibold text-foreground text-sm">{heading}</h3>
      {rows.length === 0 ? (
        <p className="py-2 text-muted-foreground text-sm">{t('reports:leaderboards.empty')}</p>
      ) : (
        <ol className="grid gap-1">
          {rows.map((row, index) => {
            const value = metric === 'profit' ? fmt.money(row.profit) : fmt.percent(row.margin);
            const valueClass =
              metric === 'profit'
                ? row.profit >= 0
                  ? 'text-success'
                  : 'text-destructive'
                : 'text-foreground';
            return (
              <li key={row.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="font-mono text-muted-foreground text-xs">{index + 1}.</span>
                  <Link
                    to="/jobs"
                    className="truncate font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:py-1"
                  >
                    {row.job_name || t('reports:table.unnamedJob', { id: row.id })}
                  </Link>
                </span>
                <span className={`shrink-0 font-mono font-semibold ${valueClass}`}>{value}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export function ProfitLeaderboards({
  topProfit,
  worstProfit,
  topMargin,
  worstMargin,
  fmt,
}: {
  topProfit: ProfitabilityRowDto[];
  worstProfit: ProfitabilityRowDto[];
  topMargin: ProfitabilityRowDto[];
  worstMargin: ProfitabilityRowDto[];
  fmt: ReportFormatters;
}) {
  const { t } = useTranslation();

  return (
    <section aria-label={t('reports:leaderboards.heading')} className="grid gap-3">
      <h2 className="font-semibold text-foreground text-xl">{t('reports:leaderboards.heading')}</h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <LeaderList
          heading={t('reports:leaderboards.topProfit')}
          rows={topProfit}
          metric="profit"
          fmt={fmt}
        />
        <LeaderList
          heading={t('reports:leaderboards.worstProfit')}
          rows={worstProfit}
          metric="profit"
          fmt={fmt}
        />
        <LeaderList
          heading={t('reports:leaderboards.topMargin')}
          rows={topMargin}
          metric="margin"
          fmt={fmt}
        />
        <LeaderList
          heading={t('reports:leaderboards.worstMargin')}
          rows={worstMargin}
          metric="margin"
          fmt={fmt}
        />
      </div>
    </section>
  );
}
